import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Tooltip,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AirIcon from '@mui/icons-material/Air';
import ImageIcon from '@mui/icons-material/Image';

import messages from '../../../static/messages';
import proposalsServices from '../../../services/proposalsServices';
import airflowItemsServices from '../../../services/airflowItemsServices';

// Drag handle component
const DragHandle = SortableHandle(() => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 1,
      bgcolor: 'grey.100',
      cursor: 'grab',
      '&:active': { cursor: 'grabbing' },
    }}
  >
    <DragIndicatorIcon color="action" />
  </Box>
));

// Sortable item component
const SortableItem = SortableElement(({ item, itemIndex, onDescriptionChange, onDescriptionBlur, onDelete }) => (
  <Card
    sx={{
      mb: 2,
      display: 'flex',
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <DragHandle />

    {/* Image - 1/3 */}
    <Box
      sx={{
        width: '33%',
        minWidth: 150,
        maxWidth: 250,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      {item.imageUrl ? (
        <Box
          component="img"
          src={item.imageUrl}
          alt={item.name}
          sx={{
            maxWidth: '100%',
            maxHeight: 150,
            objectFit: 'contain',
          }}
        />
      ) : (
        <Box sx={{ textAlign: 'center', color: 'grey.400' }}>
          <ImageIcon sx={{ fontSize: 48 }} />
          <Typography variant="caption" display="block">No image</Typography>
        </Box>
      )}
    </Box>

    {/* Content - 2/3 */}
    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {item.name}
        </Typography>
        <Tooltip title="Remove">
          <IconButton size="small" onClick={() => onDelete(itemIndex)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField
        multiline
        minRows={3}
        maxRows={8}
        fullWidth
        value={item.description || ''}
        onChange={(e) => onDescriptionChange(itemIndex, e.target.value)}
        onBlur={() => onDescriptionBlur(itemIndex)}
        placeholder="Enter description..."
        variant="outlined"
        size="small"
        sx={{ flex: 1 }}
      />
    </CardContent>
  </Card>
));

// Sortable list container
const SortableList = SortableContainer(({ items, onDescriptionChange, onDescriptionBlur, onDelete }) => (
  <Box>
    {items.map((item, index) => (
      <SortableItem
        key={item.id || `item-${index}`}
        index={index}
        item={item}
        itemIndex={index}
        onDescriptionChange={onDescriptionChange}
        onDescriptionBlur={onDescriptionBlur}
        onDelete={onDelete}
      />
    ))}
  </Box>
));

const ProposalAirflowSection = ({ proposal, id, refresh }) => {
  const [airflowItems, setAirflowItems] = useState([]); // From справочник
  const [proposalAirflows, setProposalAirflows] = useState([]); // Items in proposal
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  // Load справочник
  const loadAirflowItems = async () => {
    try {
      const response = await airflowItemsServices.getAll();
      setAirflowItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading airflow items:', error);
    }
  };

  useEffect(() => {
    loadAirflowItems();
  }, []);

  useEffect(() => {
    if (proposal?.proposalAirflows) {
      const sorted = [...proposal.proposalAirflows].sort((a, b) => a.orderIndex - b.orderIndex);
      setProposalAirflows(sorted);
    } else {
      setProposalAirflows([]);
    }
  }, [proposal?.proposalAirflows]);

  // Add item from справочник
  const handleAddItem = async (item) => {
    const newAirflow = {
      airflowItemId: item.id,
      name: item.name,
      imageUrl: item.imageUrl || null,
      description: item.description || '',
      orderIndex: proposalAirflows.length,
    };

    const updatedList = [...proposalAirflows, newAirflow];
    
    try {
      setSaving(true);
      await proposalsServices.update(id, { proposalAirflows: updatedList });
      toast.success('Airflow item added');
      setDialogOpen(false);
      refresh();
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Update description
  const handleDescriptionChange = useCallback((index, value) => {
    setProposalAirflows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], description: value };
      return updated;
    });
  }, []);

  // Save on blur
  const handleDescriptionBlur = async () => {
    try {
      await proposalsServices.update(id, { proposalAirflows });
    } catch (error) {
      toast.error('Failed to save');
      console.error(error);
    }
  };

  // Delete item
  const handleDelete = (index) => {
    confirm({
      title: messages.confirm_message.remove,
      description: 'Remove this airflow item from proposal?',
    })
      .then(async () => {
        const updated = proposalAirflows.filter((_, i) => i !== index);
        const reindexed = updated.map((item, i) => ({ ...item, orderIndex: i }));
        
        try {
          await proposalsServices.update(id, { proposalAirflows: reindexed });
          toast.success('Item removed');
          refresh();
        } catch (error) {
          toast.error(messages.errors.error_try_again);
        }
      })
      .catch(() => {});
  };

  // DnD reorder
  const handleSortEnd = async ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    
    const reordered = arrayMoveImmutable(proposalAirflows, oldIndex, newIndex);
    const reindexed = reordered.map((item, index) => ({ ...item, orderIndex: index }));
    setProposalAirflows(reindexed);
    
    try {
      await proposalsServices.update(id, { proposalAirflows: reindexed });
    } catch (error) {
      toast.error('Failed to reorder');
      refresh();
    }
  };

  // Filter out items already in proposal
  const availableItems = airflowItems.filter(
    item => !proposalAirflows.some(pa => pa.airflowItemId === item.id)
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AirIcon color="primary" />
          Airflow Items ({proposalAirflows.length})
        </Typography>
      </Box>

      {proposalAirflows.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No airflow items added. Click "Add Airflow" to add items from the catalog.
        </Typography>
      ) : (
        <SortableList
          items={proposalAirflows}
          onSortEnd={handleSortEnd}
          onDescriptionChange={handleDescriptionChange}
          onDescriptionBlur={handleDescriptionBlur}
          onDelete={handleDelete}
          useDragHandle
          lockAxis="y"
        />
      )}

      {/* Add button at bottom - like Scope of Work */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{ mt: 2 }}
        disabled={availableItems.length === 0}
      >
        Add Airflow
      </Button>

      {/* Add from catalog dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Airflow Item</DialogTitle>
        <DialogContent>
          {availableItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              All items from catalog are already added
            </Typography>
          ) : (
            <List>
              {availableItems.map((item) => (
                <ListItem
                  key={item.id}
                  button
                  onClick={() => handleAddItem(item)}
                  disabled={saving}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemAvatar>
                    {item.imageUrl ? (
                      <Avatar src={item.imageUrl} variant="rounded" />
                    ) : (
                      <Avatar variant="rounded" sx={{ bgcolor: 'grey.200' }}>
                        <AirIcon color="disabled" />
                      </Avatar>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={item.description ? (item.description.length > 80 ? item.description.substring(0, 80) + '...' : item.description) : 'No description'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProposalAirflowSection;
